/**
 * This is the main Devices page.
 *
 * Important:
 * - filterRegion is responsible for showing the filter system in the top of the page
 * - the view listens to device updates over sockets (onRemoteDeviceUpdated)
 */
'use strict'

var Marionette = require('marionette')
var Backbone = require('backbone')
var App = require('app')
var _ = require('lodash')
var Tabs = require('src/behaviors/tabs')
var PageModel = require('src/common/page-model')
var Helpers = require('src/common/helpers')
var DevicesFilter = require('src/components/devices-filter')
var DeviceTypes = require('src/common/app-constants').deviceTypes

var View = require('./view.js')
// var Search = require('./search.js')

var DevicesView = function () {
  var Controller = Marionette.Object.extend({
    initialize: function () {
      var self = this

      var pageModel = new PageModel({
        title: 'Devices',
        buttons: [
          { label: 'emergency mode', icon: true, slug: 'emergency', action: 'show:emergency', roles: ['super', 'admin'] }
        ],
        tabs: [
          { label: 'Nodes', slug: 'nodes', elementId: 'tab-nodes', roles: [] },
          { label: 'Groups', slug: 'groups', elementId: 'tab-groups', roles: [] }
        ]
      })

      // prepare collections
      self.devicesGeo = App.globalChannel.request('device:geo:entities', {pagination: false, noFetch: true})
      self.devicesCollection = App.globalChannel.request('device:entities', {pagination: true, noFetch: true})
      self.groupsCollection = App.globalChannel.request('group:entities', {pagination: true, noFetch: true})

      // listen to devices events
      App.globalChannel.on('devices:event:updated', _.bind(self.onRemoteDeviceUpdated, self))

      self.layout = self.getLayoutView(pageModel)

      // layout listeners
      self.listenTo(self.layout, 'tab:switch', _.bind(self.onTabSwitch, self))
      self.listenTo(self.layout, 'show:emergency', () => App.globalChannel.trigger('devices:emergency'))
      self.listenTo(self.layout, 'render', _.bind(self.filterRegion, self))
      //
      App.rootLayout.showChildView('mainRegion', self.layout)

      /**
       * When query parameters are given filter the devices
       */
      var urlParams = Helpers.getUrlParams(window.location.href)
      if (urlParams.state) {
        setTimeout(() => {
          self.devicesFilter.onAddFilter('state', {
            popupVisible: false,
            selectedValues: urlParams.state
          })
          self.devicesFilter.updateQuery()
        }, 0)
      }
    },

    /* Regions --------------------------------------------------------------- */
    filterRegion: function () {
      var self = this

      // setup a the filter base model and collection for the individual
      // filter sources
      var FilterSourceModel = Backbone.Model.extend({})
      var FilterSourceCollection = Backbone.Collection.extend({ model: FilterSourceModel })

      // prepare the different sources for the different filter panels
      var groupsFilterSource = App.globalChannel.request('group:entities', {pagination: false, query: {$select: 'name', $sort: 'name'}})
      var clientsFilterSource = App.globalChannel.request('client:entities', {pagination: false, query: {$select: 'name', $sort: 'name'}})
      var typeFilterSource = new FilterSourceCollection(_.map(DeviceTypes, (item) => {
        return {name: item.label, value: item.id}
      }))

      var stateFilterSource = new FilterSourceCollection([
        {name: 'online', value: 'online'},
        {name: 'offline', value: 'offline'},
        {name: 'error', value: 'error'}
      ])

      /**
       * Filters
       * -----------
       * Define the available filters
       */

      this.devicesFilter = new DevicesFilter(
        {
          region: this.layout.getRegion('filterRegion'),
          title: 'Filter devices',
          filterCache: App.devicesFilterCache, // link to an persisted object to store the selected filters etc
          filters: [
            {
              id: 'client', // unique filter id
              domain: 'client', // key which corresponds to the data parameter of the query
              name: 'client', // visual
              label: 'client', // visual
              lingualSubject: 'by',
              prependedText: 'by client',
              type: 'multi_select',
              collection: clientsFilterSource,
              collectionMap: {label: 'name', value: '_id'},
              role: ['super', 'admin']
            },
            {
              id: 'groups', // unique filter id
              domain: 'groups', // key which corresponds to the data parameter of the query
              name: 'group', // visual
              label: 'group', // visual
              lingualSubject: 'by',
              prependedText: 'by group',
              type: 'multi_select',
              collection: groupsFilterSource,
              collectionMap: {label: 'name', value: '_id'},
              role: ['super', 'admin']
            },
            {
              id: 'type',
              domain: 'type',
              name: 'type',
              label: 'device type',
              lingualSubject: 'by',
              prependedText: 'by type',
              type: 'multi_select',
              collection: typeFilterSource,
              collectionMap: {label: 'name', value: 'value'}, // might be better to be able to ignore the collectionMap
              role: []
            },
            {
              id: 'state', // unique filter id
              domain: 'state', // key which indicates the subject of thedata and correspondents to the database
              name: 'state', // visual
              label: 'device state', // visual
              lingualSubject: 'by',
              prependedText: 'which state is',
              type: 'select',
              collection: stateFilterSource,
              collectionMap: {label: 'name', value: 'value'},
              role: []
            },
            {
              id: 'location', // unique filter id
              domain: 'location', // key which indicates the subject of thedata and correspondents to the database
              name: 'location', // visual
              label: 'device location', // visual
              lingualSubject: 'where',
              prependedText: 'located near',
              type: 'location',
              role: [],
              value: {
                location: [51.6878954, 5.0574822],
                radius: 1000
              }
            },
            {
              id: 'name',
              domain: 'name',
              name: 'name',
              label: 'name',
              lingualSubject: 'contains',
              prependedText: 'by name',
              type: 'text',
              role: []
            },
            {
              id: '_id',
              domain: '_id',
              name: '_id',
              label: 'STM Id',
              lingualSubject: 'contains',
              prependedText: 'by id',
              type: 'text',
              role: []
            },
            {
              id: 'ip',
              domain: 'ip',
              name: 'ip',
              label: 'IP Address',
              lingualSubject: 'contains',
              prependedText: 'by ip',
              type: 'text',
              role: []
            },
            {
              id: 'firmwareVersion',
              domain: 'firmwareVersion',
              name: 'firmwareVersion',
              label: 'Firmware Version',
              lingualSubject: 'contains',
              prependedText: 'by firmware',
              type: 'text',
              role: []
            }
          ]
        })

      // Fires when the user changes settings in the top filter
      this.devicesFilter.on('queryUpdate', function (result) {
        // build query
        var query = {}

        // form a query which is accepted by the api based on the filters set by the user
        _.forEach(result, function (value, filterName) {
          switch (filterName) {
            case '_id':
              query[filterName] = value.trim()              
              break
            case 'location':
              query['geoLocation'] = '{near}' + value
              break
            case 'state':
              if (!value[0]) return false
              // create state query
              switch (value[0]) {
                case 'error':
                  query['error'] = true
                  break
                case 'online':
                  query['online'] = true
                  break
                case 'offline':
                  query['online'] = false
                  break
              }

              break
            case 'ip':
            case 'firmwareVersion':
            case 'name':
              query[filterName] = '{contain}' + value
              break
            default:
              if (value.length > 0) {
                query[filterName] = value.join(',')
              }
              break
          }
        })

        self.devicesGeo.fetch({data: _.clone(query)}) // clone otherwise the other fetches were affected
        self.devicesCollection.fetch({reset: true, data: query})
        self.groupsCollection.fetch({reset: true, data: query})
      })
    },
    /* Event methods --------------------------------------------------------- */
    onRemoteDeviceUpdated: function (data) {
      var self = this
      var device = self.devicesCollection.get(data._id)
      if (!device) return false
      device.set(data)
    },
    onTabSwitch: function (tab) {
      var self = this

      switch (tab.slug) {
        case 'nodes':
          App.globalChannel.trigger('devices:list', { devices: self.devicesCollection, devicesGeo: self.devicesGeo, region: self.layout.getRegion('bodyRegion')})
          break
        case 'gateways': // never implemented
        case 'groups':
          App.globalChannel.trigger('groups:list', {groups: self.groupsCollection, region: self.layout.getRegion('bodyRegion')})
          break
      }
    },
    /* Views ----------------------------------------------------------------- */
    getLayoutView: function (pageModel, model) {
      return new View.Layout({pageModel: pageModel, model: model})
    }
  })

  return new Controller()
}

module.exports = DevicesView



// WEBPACK FOOTER //
// ./js/modules/page-devices/list/index.js